import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { axios } from "../../../api/axios";
import { handleResponse } from "../../../utils/functions/http/handleResponse";
import { useAtom } from "jotai";
import { authAtom } from "../../../atoms/auth.atom";
import useModal from "antd/es/modal/useModal";
import { colors } from "../../../theme/colors";
import { Row } from "../../../components/ui/grid/row.component";
import { Button, Checkbox, Form, Input, Modal, Select, Tabs } from "antd";
import styled from "styled-components";
import { Subtitle } from "../../films/components/subtitle.component";
import { TextButton } from "../../../components/ui/text-button/text-button.component";
import { MdAdd } from "react-icons/md";
import { countries } from "../../../data/countries";
import { SubmitButton } from "../../../components/layout/submit-button/submit-button.component";
import { useForm } from "antd/es/form/Form";
import axiosLib, { AxiosError } from "axios";
import toast from "react-hot-toast";
import { v4 } from "uuid";
import { jsonToFormData } from "../../../utils/functions/misc/jsonToFormData";
import { FileUpload } from "../../../components/ui/file-upload/file-upload.component";
import { BackButton } from "../../../components/layout/back-button/back-button.component";
import { wait } from "../../../utils/functions/misc/wait";
import { config } from "../../../config/config";
import { SetState } from "../../../types/SetState";
import { Distribution } from "../../../models/distribution.model";
import { Season } from "../../../models/season.model";
import { Episode } from "../../../models/episode.model";
import { SeriesProfile } from "../../../models/series-profile.model";


const DistributeFormItem = styled(Form.Item).attrs({ layout: "vertical" })`
  width: 100%;
  max-width: 100%;
  min-width: 0 !important;
  flex: none !important;
  margin-bottom: 1.5rem !important;

  .ant-form-item-label > label {
    font-size: 1.35rem;
    font-weight: 600;
    color: ${colors.text.hex};
    height: auto;
  }
`;

const FieldStack = styled.div`
  width: 100%;
  display: block;
`;

const PlatformCheckList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  width: 100%;
`;

interface FormSchema {
    territory: string;
    territory_countries?: string[];
    platforms: string[];
    monetization_model: string[];
    information_true: boolean;
    terms_and_conditions: boolean;
    original_content: boolean;
    signature: string;
    project_id?: string;
    project_type?: "film" | "series" | "season";
    season_id?: string;
    subtitle_status?: string;
}

export const DistributeSeries = () => {
    const { id: seriesId } = useParams();

    const [auth, setAuth] = useAtom(authAtom);

    const navigate = useNavigate();

    const [modal, modalConfig] = useModal();

    const [distributionIds, setDistributionIds] = useState<(string | null)[]>([]);
    const [distributions, setDistributions] = useState<(FormSchema & Distribution)[]>([]);

    const [isMetadataSubmittedBySeason, setIsMetadataSubmittedBySeason] = useState<boolean[]>([]);

    const [activeTab, setActiveTab] = useState(1);

    const [showSubmitAsModal, setShowSubmitAsModal] = useState(false);

    const [film, setFilm] = useState<SeriesProfile | null>(null);
    const [hasShownPopup, setHasShownPopup] = useState(false);
    const [isSubtitlesAvailable, setIsSubtitlesAvailable] = useState(false);
    const [subtitles, setSubtitles] = useState<
        { language: string; file: File | string | null; id?: number }[]
    >([]);

    const [territoriesType, setTerritoriesType] = useState<
        null | "worldwide" | "excluding_these_countries" | "only_these_countries"
    >(null);

    const [trailer, setTrailer] = useState<File | null | string>(null);
    const [trailerUploaded, setTrailerUploaded] = useState<
        number | boolean | null
    >(null);
    const [trailerAbort, setTrailerAbort] = useState<AbortController | null>(
        null
    );

    const [filmVideo, setFilmVideo] = useState<File | null | string>(null);
    const [filmVideoUploaded, setFilmVideoUploaded] = useState<
        number | boolean | null
    >(null);
    const [filmVideoAbort, setFilmVideoAbort] = useState<AbortController | null>(
        null
    );

    const [checkboxes, setCheckboxes] = useState([false, false, false]);

    const [verticalTrailer, setVerticalTrailer] = useState<File | null | string>(
        null
    );
    const [verticalTrailerUploaded, setVerticalTrailerUploaded] = useState<
        number | boolean | null
    >(null);
    const [verticalTrailerAbort, setVerticalTrailerAbort] =
        useState<AbortController | null>(null);

    const [verticalVideo, setVerticalVideo] = useState<File | null | string>(
        null
    );
    const [verticalVideoUploaded, setVerticalVideoUploaded] = useState<
        number | boolean | null
    >(null);
    const [verticalVideoAbort, setVerticalVideoAbort] =
        useState<AbortController | null>(null);

    // Upload progress states (for currently active season/episode)
    const [seasonHorizontalTrailerUploaded, setSeasonHorizontalTrailerUploaded] = useState<number | boolean | null>(null);
    const [seasonVerticalTrailerUploaded, setSeasonVerticalTrailerUploaded] = useState<number | boolean | null>(null);
    const [episodeHorizontalVideoUploaded, setEpisodeHorizontalVideoUploaded] = useState<number | boolean | null>(null);
    const [episodeVerticalVideoUploaded, setEpisodeVerticalVideoUploaded] = useState<number | boolean | null>(null);

    const [censorCertificate, setCensorCertificate] = useState<
        File | string | null
    >(null);
    const [musicCueSheet, setMusicCueSheet] = useState<File | string | null>(
        null
    );

    const [agreementUrl, setAgreementUrl] = useState<string | null>(null);

    const [platforms, setPlatforms] = useState<string[]>([]);

    const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);

    const [form] = useForm<FormSchema>();
    const [form2] = useForm<{ subtitle_status: string }>();

    const [activeSeasonIndex, setActiveSeasonIndex] = useState(0);
    const [activeEpisodeIndex, setActiveEpisodeIndex] = useState(0);
    const [seasons, setSeasons] = useState<Season[]>([]);

    const onSubmitAsConfirmed = useCallback(
        async (type: "individual" | "business") => {
            const formData = new FormData();
            formData.append("submit_as", type);

            const result = await axios.put("/auth/update/user/profile", formData);
            handleResponse(result, () => {
                setAuth({
                    ...auth,
                    user: auth.user
                        ? {
                            ...auth.user,
                            submit_as: type,
                        }
                        : auth.user,
                });
            });
        },
        [auth]
    );

    const uploadFile = useCallback(
        async (
            newTrailer: File,
            video_type: "video" | "vertical_video" | "trailer" | "vertical_trailer",
            distribution_type: "series" | "season" | "episode",
            onProgress?: (progress: number) => void,
            setAbort?: SetState<AbortController | null>
        ) => {
            try {
                let idToUse: string | null = null;
                if (distribution_type === "series") idToUse = seriesId ?? null;
                else if (distribution_type === "season") idToUse = seasons[activeSeasonIndex]?.id ?? null;
                else if (distribution_type === "episode") idToUse = seasons[activeSeasonIndex]?.episodes?.[activeEpisodeIndex]?.id ?? null;
                if (!idToUse) return false;

                const abortController = new AbortController();
                if (setAbort) {
                    setAbort(abortController);
                }

                const { data: initData } = await axios.post<{
                    upload_id: string;
                    part_urls: { part_number: number; url: string }[];
                    file_name: string;
                    distribution_type?: string;
                    video_type?: string;
                    series_id?: string;
                    season_id?: string;
                    episode_id?: string;
                }>("/client/distribution/series/multipart-video", {
                    video_type,
                    distribution_type,
                    distribution_id: idToUse,
                    original_file_name: newTrailer.name,
                    file_size: newTrailer.size,
                    ...(distribution_type === "season" && { season_id: seasons[activeSeasonIndex]?.id }),
                    ...(distribution_type === "episode" && { episode_id: seasons[activeSeasonIndex]?.episodes?.[activeEpisodeIndex]?.id }),
                });

                const {
                    upload_id,
                    part_urls: partUploadUrls,
                    file_name,
                    series_id: initSeriesId,
                    season_id: initSeasonId,
                    episode_id: initEpisodeId,
                    distribution_type: initDistributionType,
                    video_type: initVideoType,
                } = initData;

                const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB per chunk
                const totalParts = Math.ceil(newTrailer.size / CHUNK_SIZE);
                const partLoaded = Array(totalParts).fill(0);

                const sleep = (ms: number) =>
                    new Promise((resolve) => setTimeout(resolve, ms));

                let lastReportedPercent = -1;
                const reportProgress = () => {
                    if (!onProgress || !newTrailer.size) return;
                    const loaded = partLoaded.reduce((sum, n) => sum + n, 0);
                    const ratio = loaded / newTrailer.size;
                    const capped = ratio >= 1 ? 0.99 : ratio;
                    const percent = Math.floor(capped * 100);
                    if (percent === lastReportedPercent) return;
                    lastReportedPercent = percent;
                    onProgress(capped);
                };

                const putPart = (
                    url: string,
                    body: Blob,
                    onByteProgress: (loaded: number) => void
                ) =>
                    new Promise<string>((resolve, reject) => {
                        const xhr = new XMLHttpRequest();
                        const onAbort = () => xhr.abort();
                        const cleanup = () =>
                            abortController.signal.removeEventListener("abort", onAbort);

                        xhr.open("PUT", url);
                        xhr.timeout = 3 * 60 * 1000;
                        xhr.upload.onprogress = (evt) => {
                            if (evt.lengthComputable) onByteProgress(evt.loaded);
                        };
                        xhr.onload = () => {
                            cleanup();
                            if (xhr.status >= 200 && xhr.status < 300) {
                                onByteProgress(body.size);
                                resolve(
                                    xhr.getResponseHeader("ETag") ||
                                        xhr.getResponseHeader("etag") ||
                                        ""
                                );
                                return;
                            }
                            reject(
                                new Error(
                                    `Upload part failed (${xhr.status || "network"})`
                                )
                            );
                        };
                        xhr.onerror = () => {
                            cleanup();
                            reject(new Error("Network error while uploading"));
                        };
                        xhr.ontimeout = () => {
                            cleanup();
                            reject(new Error("Upload timed out"));
                        };
                        xhr.onabort = () => {
                            cleanup();
                            reject(new Error("Upload aborted"));
                        };

                        if (abortController.signal.aborted) {
                            reject(new Error("Upload aborted"));
                            return;
                        }
                        abortController.signal.addEventListener("abort", onAbort);
                        // Untyped blob so the presigned URL is not sent a video Content-Type it was not signed with.
                        xhr.send(body);
                    });

                const uploadPart = async (
                    partNumber: number
                ): Promise<{ ETag: string; PartNumber: number }> => {
                    const part = partUploadUrls[partNumber];
                    if (!part?.url) {
                        throw new Error(
                            `Missing upload URL for part ${partNumber + 1}`
                        );
                    }
                    const start = partNumber * CHUNK_SIZE;
                    const end = Math.min(start + CHUNK_SIZE, newTrailer.size);
                    const blobPart = new Blob([newTrailer.slice(start, end)]);

                    let retries = 3;
                    let lastError: unknown;
                    while (retries > 0) {
                        try {
                            const etag = await putPart(part.url, blobPart, (loaded) => {
                                partLoaded[partNumber] = loaded;
                                reportProgress();
                            });
                            partLoaded[partNumber] = blobPart.size;
                            reportProgress();
                            return {
                                ETag: etag,
                                PartNumber: partNumber + 1,
                            };
                        } catch (err) {
                            lastError = err;
                            if (
                                abortController.signal.aborted ||
                                (err instanceof Error && err.message === "Upload aborted")
                            ) {
                                throw err;
                            }
                            retries -= 1;
                            partLoaded[partNumber] = 0;
                            reportProgress();
                            if (retries === 0) break;
                            console.warn(
                                `Retrying part ${partNumber + 1}. Attempts left: ${retries}`
                            );
                            await sleep(1500);
                        }
                    }
                    throw lastError instanceof Error
                        ? lastError
                        : new Error(`Failed to upload part ${partNumber + 1}`);
                };

                const concurrentLimit = 3;
                const partETags: { ETag: string; PartNumber: number }[] = [];
                const pool = Array(concurrentLimit).fill(null); // Create a pool

                let index = 0;

                await Promise.all(
                    pool.map(async () => {
                        while (index < totalParts) {
                            const currentIndex = index;
                            index += 1;
                            const partETag = await uploadPart(currentIndex);
                            if (partETag) {
                                partETags.push(partETag);
                            }
                        }
                    })
                );

                await axios.post("/client/complete/distribution/multipart-video", {
                    upload_id,
                    parts: partETags.sort((a, b) => a.PartNumber - b.PartNumber),
                    key: file_name,
                    distribution_type: initDistributionType ?? distribution_type,
                    video_type: initVideoType ?? video_type,
                    ...(initSeriesId ? { series_id: initSeriesId } : {}),
                    ...(initSeasonId ? { season_id: initSeasonId } : {}),
                    ...(initEpisodeId ? { episode_id: initEpisodeId } : {}),
                });

                // After successful upload, fetch updated distribution data
                const { data: updatedDistribution } = await axios.get<
                    (FormSchema & Distribution)[]
                >("/client/distribution", {
                    headers: {
                        "X-Project-Id": seriesId,
                        "X-Project-Type": "series",
                    },
                });
                setDistributions(updatedDistribution || []);

                // Map videos to the correct places based on video_category
                if (updatedDistribution?.length) {
                    updatedDistribution.forEach((dist) => {
                        (dist.videos ?? []).forEach((video: any) => {
                            const videoUrl = video.video_url || video.video_file_name;
                            const videoCategory = video.video_category || video.type;

                            // Handle series-level trailers
                            if (videoCategory === "series") {
                                if (video.video_type === "primary-trailer") {
                                    setTrailer(videoUrl);
                                } else if (video.video_type === "additional-trailer") {
                                    setVerticalTrailer(videoUrl);
                                }
                            }
                        });
                    });

                    // Map season and episode videos
                    setSeasons((prevSeasons) => {
                        const newSeasons = prevSeasons.map((season) => ({
                            ...season,
                            episodes: season.episodes.map((ep) => ({ ...ep })),
                        }));

                        updatedDistribution.forEach((dist) => {
                            (dist.videos ?? []).forEach((video: any) => {
                                const videoUrl = video.video_url || video.video_file_name;
                                const videoCategory = video.video_category || video.type;

                                if (videoCategory === "season" && video.season_number) {
                                    // Convert 1-indexed season_number to 0-indexed array index
                                    const seasonIndex = video.season_number - 1;
                                    if (seasonIndex >= 0 && seasonIndex < newSeasons.length) {
                                        if (video.video_type === "primary-trailer") {
                                            newSeasons[seasonIndex].horizontal_trailer = [{ url: videoUrl, type: 0 }];
                                        } else if (video.video_type === "additional-trailer") {
                                            newSeasons[seasonIndex].vertical_trailer = [{ url: videoUrl, type: 1 }];
                                        }
                                    }
                                }

                                if (videoCategory === "episode" && video.season_number && video.episode_number) {
                                    // Convert 1-indexed numbers to 0-indexed array indices
                                    const seasonIndex = video.season_number - 1;
                                    const episodeIndex = video.episode_number - 1;
                                    if (
                                        seasonIndex >= 0 &&
                                        seasonIndex < newSeasons.length &&
                                        episodeIndex >= 0 &&
                                        episodeIndex < newSeasons[seasonIndex].episodes.length
                                    ) {
                                        if (video.video_type === "primary-video") {
                                            newSeasons[seasonIndex].episodes[episodeIndex].horizontal_video = [{ url: videoUrl, type: 0 }];
                                        } else if (video.video_type === "additional-video") {
                                            newSeasons[seasonIndex].episodes[episodeIndex].vertical_video = [{ url: videoUrl, type: 1 }];
                                        }
                                    }
                                }
                            });
                        });

                        return newSeasons;
                    });
                }

                onProgress?.(1);
                return true;
            } catch (e) {
                const aborted =
                    axiosLib.isCancel(e) ||
                    (e instanceof Error && e.message === "Upload aborted");
                if (!aborted) {
                    if (axiosLib.isAxiosError(e)) {
                        handleResponse(e);
                    } else {
                        toast.error(
                            e instanceof Error && e.message
                                ? e.message
                                : "Upload failed. Please try again."
                        );
                    }
                }
                return false;
            }
        },
        [seriesId, seasons, activeSeasonIndex, activeEpisodeIndex]
    );

    const deleteUploadedVideo = useCallback(
        async (
            video_type: "video" | "vertical_video" | "trailer" | "vertical_trailer",
            distribution_type: "series" | "season" | "episode"
        ) => {
            try {
                let idToUse: string | null = null;
                if (distribution_type === "series") idToUse = seriesId ?? null;
                else if (distribution_type === "season") idToUse = seasons[activeSeasonIndex]?.id ?? null;
                else idToUse = seasons[activeSeasonIndex]?.episodes?.[activeEpisodeIndex]?.id ?? null;
                if (!idToUse) return false;

                await axios.post("/client/distribution/series/delete-video", {
                    video_type,
                    distribution_type,
                    distribution_id: idToUse,
                });
                const { data: updatedDistribution } = await axios.get<
                    (FormSchema & Distribution)[]
                >("/client/distribution", {
                    headers: {
                        "X-Project-Id": seriesId,
                        "X-Project-Type": "series",
                    },
                });
                setDistributions(updatedDistribution || []);
                return true;
            } catch (e) {
                if (axiosLib.isAxiosError(e)) {
                    handleResponse(e);
                } else {
                    toast.error("Could not remove that upload. Please try again.");
                }
                return false;
            }
        },
        [seriesId, seasons, activeSeasonIndex, activeEpisodeIndex]
    );

    useEffect(() => {
        // Placeholder fetch for series details
        axios
            .get<{ title: string; custom_name: string }>("/client/series", {
                headers: {
                    "X-SERIES-ID": seriesId,
                },
            })
            .then((response) => {
                setFilm({ ...(response.data as any) });
                setSeasons((response.data as SeriesProfile).seasons as any);
            })
            .catch(() => null);

        // Fetch distribution for series
        axios
            .get<(FormSchema & Distribution)[]>("/client/distribution", {
                headers: {
                    "X-Project-Id": seriesId,
                    "X-Project-Type": "series",
                },
            })
            .then((resp) => {
                const arr = resp.data || [];
                setDistributions(arr);
                setDistributionIds(arr.map((d) => d.id));
                setIsMetadataSubmittedBySeason(arr.map(() => true));

                // Map videos to the correct places based on video_category
                if (arr.length) {
                    // Handle series-level trailers first
                    arr.forEach((dist) => {
                        (dist.videos ?? []).forEach((video: any) => {
                            const videoUrl = video.video_url || video.video_file_name;
                            const videoCategory = video.video_category || video.type;

                            if (videoCategory === "series") {
                                if (video.video_type === "primary-trailer") {
                                    setTrailer(videoUrl);
                                } else if (video.video_type === "additional-trailer") {
                                    setVerticalTrailer(videoUrl);
                                }
                            }
                        });
                    });

                    // Map season and episode videos
                    setSeasons((prevSeasons) => {
                        const newSeasons = prevSeasons.map((season) => ({
                            ...season,
                            episodes: season.episodes.map((ep) => ({ ...ep })),
                        }));

                        arr.forEach((dist) => {
                            (dist.videos ?? []).forEach((video: any) => {
                                const videoUrl = video.video_url || video.video_file_name;
                                const videoCategory = video.video_category || video.type;

                                if (videoCategory === "season" && video.season_number) {
                                    // Convert 1-indexed season_number to 0-indexed array index
                                    const seasonIndex = video.season_number - 1;
                                    if (seasonIndex >= 0 && seasonIndex < newSeasons.length) {
                                        if (video.video_type === "primary-trailer") {
                                            newSeasons[seasonIndex].horizontal_trailer = [{ url: videoUrl, type: 0 }];
                                        } else if (video.video_type === "additional-trailer") {
                                            newSeasons[seasonIndex].vertical_trailer = [{ url: videoUrl, type: 1 }];
                                        }
                                    }
                                }

                                if (videoCategory === "episode" && video.season_number && video.episode_number) {
                                    // Convert 1-indexed numbers to 0-indexed array indices
                                    const seasonIndex = video.season_number - 1;
                                    const episodeIndex = video.episode_number - 1;
                                    if (
                                        seasonIndex >= 0 &&
                                        seasonIndex < newSeasons.length &&
                                        episodeIndex >= 0 &&
                                        episodeIndex < newSeasons[seasonIndex].episodes.length
                                    ) {
                                        if (video.video_type === "primary-video") {
                                            newSeasons[seasonIndex].episodes[episodeIndex].horizontal_video = [{ url: videoUrl, type: 0 }];
                                        } else if (video.video_type === "additional-video") {
                                            newSeasons[seasonIndex].episodes[episodeIndex].vertical_video = [{ url: videoUrl, type: 1 }];
                                        }
                                    }
                                }
                            });
                        });

                        return newSeasons;
                    });
                }

                // Populate form and UI for current season if available
                const current = arr[activeSeasonIndex];
                if (current) {
                    setActiveTab(2);
                    setPlatforms(current.platforms ?? []);
                    setTerritoriesType(current.territory as any);
                    const subtitles = (current.videos ?? []).reduce(
                        (list, { subtitles }) => [...list, ...(subtitles ?? [])],
                        [] as { file: string; language: string; id: number }[]
                    );
                    setSubtitles(subtitles);

                    if (current.subtitle_status) {
                        form2.setFieldValue("subtitle_status", current.subtitle_status);
                        setIsSubtitlesAvailable(current.subtitle_status === "available");
                    }
                    setAgreementUrl(current.agreement_url ?? null);

                    // Note: Series-level trailers are already set above in the video mapping loop
                    // These lines are kept for backwards compatibility with old API formats
                    const primaryVideo = (current.videos ?? []).find(
                        (video: any) => video.video_type === "primary-video" && (video.video_category || video.type) !== "episode"
                    ),
                        additionalVideo = (current.videos ?? []).find(
                            (video: any) => video.video_type === "additional-video" && (video.video_category || video.type) !== "episode"
                        );
                    if (primaryVideo) setFilmVideo(primaryVideo.video_id ?? null);
                    if (additionalVideo) setVerticalVideo(additionalVideo.video_id ?? null);
                    setCensorCertificate(current.censor_certificate ?? null);
                    setMusicCueSheet(current.music_cue_sheet ?? null);

                    const data = {
                        ...current,
                        information_true: true,
                        terms_and_conditions: true,
                        original_content: true,
                    } as any;
                    if (data.territory_countries)
                        data.territory_countries = data.territory_countries
                            .map(
                                (code: string) =>
                                    countries.find(
                                        ({ value }) =>
                                            value.split("-")[1].toLowerCase() ===
                                            code.toUpperCase()?.toLowerCase()
                                    )?.value
                            )
                            .filter((el: any) => !!el) as string[];
                    data.monetization_model = data.monetization_models ?? [];

                    form.setFieldsValue(data as any);
                    setCheckboxes([true, true, true]);
                }
            })
            .catch(() => null);
    }, [seriesId, form]);

    // Update form and agreement when active season changes and distributions are loaded
    useEffect(() => {
        const current = distributions[activeSeasonIndex];
        if (!current) return;

        setPlatforms(current.platforms ?? []);
        setTerritoriesType(current.territory as any);
        const data = {
            ...current,
            information_true: true,
            terms_and_conditions: true,
            original_content: true,
        } as any;
        if (data.territory_countries)
            data.territory_countries = data.territory_countries
                .map(
                    (code: string) =>
                        countries.find(
                            ({ value }) =>
                                value.split("-")[1].toLowerCase() ===
                                code.toUpperCase()?.toLowerCase()
                        )?.value
                )
                .filter((el: any) => !!el) as string[];
        data.monetization_model = data.monetization_models ?? [];
        form.setFieldsValue(data as any);
        form2.setFieldValue("subtitle_status", current.subtitle_status ?? "not_available");
        setAgreementUrl(current.agreement_url ?? null);
    }, [activeSeasonIndex, distributions, form, form2]);

    useEffect(() => {
        if (auth.user && !hasShownPopup) {
            // IMPORTANT uncomment once the backend issue with update persistence is fixed
            if (
                !auth.user.address_line1 &&
                (!auth.user.phone_number || !auth.user.phone_code)
            ) {
                toast.error(
                    "Please enter an address and phone number for your profile before submitting a distribution!"
                );
                wait(500).then(() =>
                    navigate(`/profile?on_submit=/s/${seriesId}/distribute`)
                );
                return;
            } else if (!auth.user.address_line1) {
                toast.error(
                    "Please enter an address for your profile before submitting a distribution!"
                );
                wait(500).then(() =>
                    navigate(`/profile?on_submit=/s/${seriesId}/distribute`)
                );
                return;
            } else if (!auth.user.phone_number || !auth.user.phone_code) {
                toast.error(
                    "Please enter a phone number for your profile before submitting a distribution!"
                );
                wait(500).then(() =>
                    navigate(`/profile?on_submit=/s/${seriesId}/distribute`)
                );
                return;
            } else if (!auth.user.phone_verified) {
                toast.error(
                    "Please verify your phone number for your profile before submitting a distribution!"
                );
                wait(500).then(() =>
                    window.location.replace(
                        `${config.auth_url}/verify?source=distribute&r=${config.base_url}/s/${seriesId}/distribute`
                    )
                );
            }

            if (auth.user.submit_as === "unclassified") {
                setHasShownPopup(true);
                setShowSubmitAsModal(true);
            }
        }
    }, [auth, hasShownPopup]);

    const activeDistribution = distributions[activeSeasonIndex];
    const seasonNumber = seasons[activeSeasonIndex]?.season_number;
    const episodeNumber =
        seasons[activeSeasonIndex]?.episodes?.[activeEpisodeIndex]?.episode_number;

    const findDistVideo = (
        videoType:
            | "primary-video"
            | "additional-video"
            | "primary-trailer"
            | "additional-trailer",
        scope: "series" | "season" | "episode"
    ) => {
        const videos = activeDistribution?.videos ?? [];
        return videos.find((v: { video_type?: string; video_category?: string; type?: string; season_number?: number; episode_number?: number }) => {
            if (v.video_type !== videoType) return false;
            const cat = v.video_category || v.type;
            if (scope === "series") return cat === "series";
            if (scope === "season")
                return cat === "season" && v.season_number === seasonNumber;
            return (
                cat === "episode" &&
                v.season_number === seasonNumber &&
                v.episode_number === episodeNumber
            );
        });
    };

    const uploadInProgress = (value: number | boolean | null) =>
        typeof value === "number" && value < 1;

    const slotReady = (progress: number | boolean | null, fileName?: string | null) => {
        const inProgress = uploadInProgress(progress);
        const failed = progress === false;
        return (
            progress === true ||
            progress === 1 ||
            (!!fileName && !inProgress && !failed)
        );
    };

    const seriesTrailerReady = slotReady(
        trailerUploaded,
        findDistVideo("primary-trailer", "series")?.video_file_name ||
            (typeof trailer === "string" ? trailer : undefined)
    );
    const seasonTrailerReady = slotReady(
        seasonHorizontalTrailerUploaded,
        findDistVideo("primary-trailer", "season")?.video_file_name ||
            seasons[activeSeasonIndex]?.horizontal_trailer?.[0]?.file?.name ||
            seasons[activeSeasonIndex]?.horizontal_trailer?.[0]?.url
    );
    const activeEpisodes = seasons[activeSeasonIndex]?.episodes ?? [];
    const seasonVideos = (activeDistribution?.videos ?? []) as {
        video_type?: string;
        video_category?: string;
        type?: string;
        episode_number?: number;
        video_file_name?: string;
    }[];
    const episodesReady =
        activeEpisodes.length > 0 &&
        activeEpisodes.every((episode, index) => {
            const savedName =
                seasonVideos.find((video) => {
                    const category = video.video_category || video.type;
                    return (
                        video.video_type === "primary-video" &&
                        category === "episode" &&
                        video.episode_number === episode.episode_number
                    );
                })?.video_file_name ||
                episode.horizontal_video?.[0]?.file?.name ||
                episode.horizontal_video?.[0]?.url;
            if (index === activeEpisodeIndex) {
                return slotReady(episodeHorizontalVideoUploaded, savedName);
            }
            return !!savedName;
        });
    const uploadsInProgress =
        isSubmitDisabled ||
        [
            trailerUploaded,
            verticalTrailerUploaded,
            seasonHorizontalTrailerUploaded,
            seasonVerticalTrailerUploaded,
            episodeHorizontalVideoUploaded,
            episodeVerticalVideoUploaded,
        ].some(uploadInProgress);
    const requiredVideosReady =
        seriesTrailerReady && seasonTrailerReady && episodesReady;

    const isRightsLocked = isMetadataSubmittedBySeason[activeSeasonIndex];

    return (
        <Main>
            <BackButton />
            {modalConfig}
            <Modal
                closable={false}
                open={showSubmitAsModal}
                title="Submit as individual / business"
                footer={
                    <Row
                        $full
                        $gap={10}
                        style={{ justifyContent: "flex-end", marginTop: 20 }}
                    >
                        <Button
                            type="dashed"
                            onClick={() => {
                                onSubmitAsConfirmed("individual");
                                setShowSubmitAsModal(false);
                            }}
                        >
                            Individual
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => {
                                axios
                                    .get("/auth/business/profile")
                                    .then(() => {
                                        onSubmitAsConfirmed("business");
                                        setShowSubmitAsModal(false);
                                    })
                                    .catch((e) => {
                                        setShowSubmitAsModal(false);
                                        navigate(`/b?r=/s/${seriesId}/distribute`);
                                    });
                            }}
                        >
                            Business
                        </Button>
                    </Row>
                }
            >
                Do you want to submit this and future distributions as a business or as
                an individual?
                <br />
                <br />
                <span style={{ color: colors.error.hex }}>
                    NOTE: THIS CHOICE CANNOT BE CHANGED.
                </span>
            </Modal>
            <DistributeHero>
                <HeroTop>
                    <Title>Submit for distribution</Title>
                    {agreementUrl ? (
                        <Button type="default" size="large" onClick={() => window.open(agreementUrl)}>
                            Open agreement
                        </Button>
                    ) : null}
                    <SeasonToolbar>
                        <DistributeFormItem label="Season">
                            <Select
                                size="large"
                                placeholder="Select season"
                                value={activeSeasonIndex}
                                onSelect={(i) => {
                                    setActiveSeasonIndex(i);
                                    setActiveTab(distributionIds[i] ? 2 : 1);
                                }}
                                options={seasons.map((season, i) => ({
                                    label: season.season_title || `Season ${i + 1}`,
                                    value: i,
                                }))}
                            />
                        </DistributeFormItem>
                    </SeasonToolbar>
                </HeroTop>
                <Paragraph>
                    You are submitting{" "}
                    <SeriesName>{film?.title ?? "Untitled series"}</SeriesName>.
                    After uploading files, wait until each upload finishes. Do not reload or
                    close this page, or turn off your device, while uploads are in progress.
                </Paragraph>
            </DistributeHero>
            <Tabs
                className="distribute-tabs"
                destroyInactiveTabPane
                animated={{ inkBar: true, tabPane: false }}
                activeKey={activeTab.toString()}
                onChange={(activeKey) => setActiveTab(+activeKey)}
                items={[
                    {
                        key: "1",
                        label: "Rights Management",
                        children: (
                            <Form
                                layout="vertical"
                                form={form}
                                className="distribute-form distribute-rights-form"
                            >
                                <RightsShell>
                                    <RightsColumn>
                                    <FieldStack>
                                        <DistributeFormItem
                                            label="Territory"
                                            name="territory"
                                            required
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please select a territory option!",
                                                },
                                            ]}
                                        >
                                            <Select
                                                size="large"
                                                placeholder="Select a territory management option"
                                                disabled={isRightsLocked}
                                                showSearch
                                                onSelect={(type) => {
                                                    setTerritoriesType(type);
                                                }}
                                                options={[
                                                    { label: "Worldwide", value: "worldwide" },
                                                    {
                                                        label: "Worldwide, excluding select countries",
                                                        value: "excluding_these_countries",
                                                    },
                                                    {
                                                        label: "Only select countries",
                                                        value: "only_these_countries",
                                                    },
                                                ]}
                                            />
                                        </DistributeFormItem>
                                    </FieldStack>
                                    {territoriesType !== "worldwide" ? (
                                        <FieldStack>
                                            <DistributeFormItem
                                                name="territory_countries"
                                                label={`Countries to ${territoriesType
                                                    ? territoriesType === "only_these_countries"
                                                        ? "include"
                                                        : "exclude"
                                                    : "include/exclude"
                                                    }`}
                                                required
                                                rules={[
                                                    {
                                                        required: true,
                                                        message:
                                                            "Please select countries to include/exclude!",
                                                    },
                                                ]}
                                            >
                                                <Select
                                                    size="large"
                                                    disabled={isRightsLocked}
                                                    placeholder={`Select countries to ${territoriesType
                                                        ? territoriesType === "only_these_countries"
                                                            ? "include"
                                                            : "exclude"
                                                        : "include/exclude"
                                                        }`}
                                                    showSearch
                                                    allowClear
                                                    options={countries}
                                                    mode="multiple"
                                                />
                                            </DistributeFormItem>
                                        </FieldStack>
                                    ) : null}
                                    <FieldStack>
                                        <DistributeFormItem
                                            label="Monetisation model"
                                            name="monetization_model"
                                            required
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please select a monetisation model!",
                                                },
                                            ]}
                                        >
                                            <Select
                                                size="large"
                                                placeholder="Select monetisation model"
                                                showSearch
                                                disabled={isRightsLocked}
                                                options={[
                                                    {
                                                        label: "AVOD (advertising-based video on demand)",
                                                        value: "avod",
                                                    },
                                                ]}
                                            />
                                        </DistributeFormItem>
                                    </FieldStack>
                                    <FieldStack>
                                        <DistributeFormItem
                                            label="Platforms"
                                            name="platforms"
                                            initialValue={["pf_owned"]}
                                        >
                                            <Checkbox.Group defaultValue={["pf_owned"]}>
                                                <PlatformCheckList>
                                                    <Checkbox value="pf_owned" disabled checked>
                                                        PF owned platforms (its website, mobile apps, apps
                                                        on other platforms, app-in-app, integrations,
                                                        etc.)
                                                    </Checkbox>
                                                    <Checkbox value="pf_digital" disabled={isRightsLocked}>
                                                        All digital platforms including but not limited to
                                                        internet, mobile, IPTV, etc.
                                                    </Checkbox>
                                                    <Checkbox value="pf_branded" disabled={isRightsLocked}>
                                                        PF branded channels on other platforms including
                                                        but not limited to YouTube, Facebook, etc.
                                                    </Checkbox>
                                                    <Checkbox value="pf_syndication" disabled={isRightsLocked}>
                                                        Syndication across platforms excluding YouTube and
                                                        Facebook.
                                                    </Checkbox>
                                                    <Checkbox value="pf_tv" disabled={isRightsLocked}>
                                                        TV and broadcast
                                                    </Checkbox>
                                                    <Checkbox value="pf_theatre" disabled={isRightsLocked}>
                                                        Theatrical, festival, public/private screenings
                                                    </Checkbox>
                                                </PlatformCheckList>
                                            </Checkbox.Group>
                                        </DistributeFormItem>
                                    </FieldStack>
                                    </RightsColumn>
                                    <RightsColumn>
                                        <AckPanel>
                                        <AckHeading>
                                            <RequiredStar>*</RequiredStar>
                                            Acknowledgement
                                        </AckHeading>
                                        <FieldStack>
                                            <DistributeFormItem name="information_true">
                                                <Checkbox
                                                    disabled={isRightsLocked}
                                                    checked={checkboxes[0]}
                                                    onChange={(evt) => {
                                                        form.setFieldValue(
                                                            "information_true",
                                                            evt.target.checked
                                                        );

                                                        const newCheckboxes = [...checkboxes];
                                                        newCheckboxes[0] = evt.target.checked;

                                                        setCheckboxes(newCheckboxes);
                                                    }}
                                                >
                                                    All information submitted above is true, correct and to
                                                    the best of my/our knowledge. I/We further accept
                                                    responsibility for the accuracy of all the information
                                                    provided by me/us.
                                                </Checkbox>
                                            </DistributeFormItem>
                                        </FieldStack>
                                        <FieldStack>
                                            <DistributeFormItem name="terms_and_conditions">
                                                <Checkbox
                                                    disabled={isRightsLocked}
                                                    checked={checkboxes[1]}
                                                    onChange={(evt) => {
                                                        form.setFieldValue(
                                                            "terms_and_conditions",
                                                            evt.target.checked
                                                        );

                                                        const newCheckboxes = [...checkboxes];
                                                        newCheckboxes[1] = evt.target.checked;

                                                        setCheckboxes(newCheckboxes);
                                                    }}
                                                >
                                                    I/We hereby declare that we have read the rules, terms
                                                    and regulations of the Distribution and hereby agree and
                                                    accept the same.
                                                </Checkbox>
                                            </DistributeFormItem>
                                        </FieldStack>
                                        <FieldStack>
                                            <DistributeFormItem name="original_content">
                                                <Checkbox
                                                    disabled={isRightsLocked}
                                                    checked={checkboxes[2]}
                                                    onChange={(evt) => {
                                                        form.setFieldValue(
                                                            "original_content",
                                                            evt.target.checked
                                                        );
                                                        const newCheckboxes = [...checkboxes];
                                                        newCheckboxes[2] = evt.target.checked;

                                                        setCheckboxes(newCheckboxes);
                                                    }}
                                                >
                                                    I/We hereby declare that the content is original and
                                                    I/We am/are authorised to deal with the above content,
                                                    enter into this agreement by accepting all the terms and
                                                    rules including on behalf of the other right holders, if
                                                    any. Signature
                                                </Checkbox>
                                            </DistributeFormItem>
                                        </FieldStack>
                                        <FieldStack>
                                            <DistributeFormItem
                                                label="Signature"
                                                name="signature"
                                                required
                                                rules={[{ required: true }]}
                                            >
                                                <Input
                                                    size="large"
                                                    disabled={isRightsLocked}
                                                    placeholder="Type in your signature"
                                                    style={{ fontWeight: "600" }}
                                                />
                                            </DistributeFormItem>
                                        </FieldStack>
                                        <RightsSubmit>
                                            <SubmitButton
                                                variant="inline"
                                                label="Save rights & continue"
                                                disabled={isRightsLocked}
                                                onClick={async () => {
                                                    try {
                                                        form.submit();
                                                        await form.validateFields();

                                                        const rightsManagementBody = form.getFieldsValue();
                                                        if (
                                                            !rightsManagementBody.information_true ||
                                                            !rightsManagementBody.original_content ||
                                                            !rightsManagementBody.terms_and_conditions
                                                        )
                                                            return toast.error(
                                                                "Please check all the required acknowledgement checkboxes!"
                                                            );
                                                        if (!rightsManagementBody.signature)
                                                            return toast.error("Please enter your signature!");

                                                        modal.confirm({
                                                            title: "Submit Rights Management",
                                                            content:
                                                                "Please confirm if you want to submit rights management details and move on to the next step to upload assets. You will be able to edit, if necessary, after the upload is completed.",
                                                            okText: "Submit and continue",
                                                            onOk: () => {
                                                                rightsManagementBody.project_type = "season";
                                                                rightsManagementBody.project_id = seasons[activeSeasonIndex]?.id;
                                                                rightsManagementBody.territory_countries = (
                                                                    rightsManagementBody.territory_countries ?? []
                                                                ).map((country) =>
                                                                    country.split("-")[1].toLowerCase()
                                                                );

                                                                axios
                                                                    .post<{ id: string }>(
                                                                        "/client/distribution",
                                                                        rightsManagementBody
                                                                    )
                                                                    .then((resp) => {
                                                                        toast.success(
                                                                            "Rights data saved successfully!"
                                                                        );
                                                                        setIsMetadataSubmittedBySeason((prev) => {
                                                                            const next = [...prev];
                                                                            next[activeSeasonIndex] = true;
                                                                            return next;
                                                                        });
                                                                        setDistributionIds((prev) => {
                                                                            const next = [...prev];
                                                                            next[activeSeasonIndex] = resp.data.id;
                                                                            return next;
                                                                        });
                                                                        setActiveTab(2);
                                                                    })
                                                                    .catch(handleResponse);
                                                            },
                                                        });
                                                    } catch (e) {
                                                        if (e instanceof AxiosError) {
                                                            handleResponse(e);
                                                        } else {
                                                            toast.error("Please fix the errors in the form!");
                                                        }
                                                    }
                                                }}
                                            />
                                        </RightsSubmit>
                                        </AckPanel>
                                    </RightsColumn>
                                </RightsShell>
                            </Form>
                        ),
                    },
                    {
                        key: "2",
                        label: "Uploads",
                        disabled: !isMetadataSubmittedBySeason[activeSeasonIndex],
                        children: (
                            <>
                            <UploadShell
                                className="distribute-uploads"
                                $columns={activeSeasonIndex === 0 ? 3 : 2}
                            >
                                {activeSeasonIndex === 0 ? (
                                    <UploadSection>
                                        <UploadSectionTitle>Series trailers</UploadSectionTitle>
                                        <UploadSectionHint>
                                            Shown across the series. Upload once on the first season tab.
                                        </UploadSectionHint>
                                        <VideoQuadGrid>
                                            <VideoField>
                                                <DistributeFormItem label="Series horizontal trailer (required)">
                                                    <FileUpload
                                                        variant="dropzone"
                                                        file={trailer instanceof File ? trailer : null}
                                                        fileName={
                                                            findDistVideo("primary-trailer", "series")
                                                                ?.video_file_name ??
                                                            (typeof trailer === "string" ? trailer : undefined)
                                                        }
                                                        fileUrl={
                                                            findDistVideo("primary-trailer", "series")?.video_id
                                                                ? `https://iframe.mediadelivery.net/embed/102339/${findDistVideo("primary-trailer", "series")!.video_id}?autoplay=false`
                                                                : null
                                                        }
                                                        onChange={async (file) => {
                                                            if (file) {
                                                                setTrailer(file);
                                                                toast.success("Uploading trailer... Please wait.");
                                                                setIsSubmitDisabled(true);
                                                                setTrailerUploaded(0);
                                                                const ok = await uploadFile(
                                                                    file,
                                                                    "trailer",
                                                                    "series",
                                                                    (p) => setTrailerUploaded(p),
                                                                    setTrailerAbort
                                                                );
                                                                setIsSubmitDisabled(false);
                                                                if (ok) {
                                                                    setTrailerUploaded(true);
                                                                    toast.success("Trailer uploaded.");
                                                                } else {
                                                                    setTrailer(null);
                                                                    setTrailerUploaded(false);
                                                                }
                                                            } else {
                                                                const removed = await deleteUploadedVideo("trailer", "series");
                                                                if (!removed) return;
                                                                setTrailer(null);
                                                                setTrailerUploaded(null);
                                                            }
                                                        }}
                                                        accept="video/mp4"
                                                        uploaded={trailerUploaded}
                                                    />
                                                </DistributeFormItem>
                                            </VideoField>
                                            <VideoField>
                                                <DistributeFormItem label="Series vertical trailer (optional)">
                                                    <FileUpload
                                                        variant="dropzone"
                                                        file={
                                                            verticalTrailer instanceof File
                                                                ? verticalTrailer
                                                                : null
                                                        }
                                                        fileName={
                                                            findDistVideo("additional-trailer", "series")
                                                                ?.video_file_name ??
                                                            (typeof verticalTrailer === "string"
                                                                ? verticalTrailer
                                                                : undefined)
                                                        }
                                                        fileUrl={
                                                            findDistVideo("additional-trailer", "series")
                                                                ?.video_id
                                                                ? `https://iframe.mediadelivery.net/embed/102339/${findDistVideo("additional-trailer", "series")!.video_id}?autoplay=false`
                                                                : null
                                                        }
                                                        onChange={async (file) => {
                                                            if (file) {
                                                                setVerticalTrailer(file);
                                                                toast.success("Uploading trailer... Please wait.");
                                                                setIsSubmitDisabled(true);
                                                                setVerticalTrailerUploaded(0);
                                                                const ok = await uploadFile(
                                                                    file,
                                                                    "vertical_trailer",
                                                                    "series",
                                                                    (p) => setVerticalTrailerUploaded(p),
                                                                    setVerticalTrailerAbort
                                                                );
                                                                setIsSubmitDisabled(false);
                                                                if (ok) {
                                                                    setVerticalTrailerUploaded(true);
                                                                    toast.success("Trailer uploaded.");
                                                                } else {
                                                                    setVerticalTrailer(null);
                                                                    setVerticalTrailerUploaded(false);
                                                                }
                                                            } else {
                                                                const removed = await deleteUploadedVideo("vertical_trailer", "series");
                                                                if (!removed) return;
                                                                setVerticalTrailer(null);
                                                                setVerticalTrailerUploaded(null);
                                                            }
                                                        }}
                                                        accept="video/mp4"
                                                        uploaded={verticalTrailerUploaded}
                                                    />
                                                </DistributeFormItem>
                                            </VideoField>
                                        </VideoQuadGrid>
                                    </UploadSection>
                                ) : null}

                                <UploadSection>
                                    <UploadSectionTitle>Season trailers</UploadSectionTitle>
                                    <UploadSectionHint>
                                        Trailers for{" "}
                                        {seasons[activeSeasonIndex]?.season_title ||
                                            `Season ${activeSeasonIndex + 1}`}
                                        .
                                    </UploadSectionHint>
                                    <VideoQuadGrid>
                                        <VideoField>
                                            <DistributeFormItem label="Season horizontal trailer (required)">
                                                <FileUpload
                                                    variant="dropzone"
                                                    file={
                                                        seasons[activeSeasonIndex]?.horizontal_trailer?.[0]
                                                            ?.file || null
                                                    }
                                                    fileName={
                                                        findDistVideo("primary-trailer", "season")
                                                            ?.video_file_name ||
                                                        seasons[activeSeasonIndex]?.horizontal_trailer?.[0]
                                                            ?.file?.name ||
                                                        undefined
                                                    }
                                                    fileUrl={
                                                        findDistVideo("primary-trailer", "season")?.video_id
                                                            ? `https://iframe.mediadelivery.net/embed/102339/${findDistVideo("primary-trailer", "season")!.video_id}?autoplay=false`
                                                            : null
                                                    }
                                                    onChange={async (file) => {
                                                        const seasonIndex = activeSeasonIndex;
                                                        if (!seasons[seasonIndex]) return;
                                                        if (!file) {
                                                            const removed = await deleteUploadedVideo("trailer", "season");
                                                            if (!removed) return;
                                                            setSeasons((prev) =>
                                                                prev.map((season, i) =>
                                                                    i === seasonIndex
                                                                        ? { ...season, horizontal_trailer: [] }
                                                                        : season
                                                                )
                                                            );
                                                            setSeasonHorizontalTrailerUploaded(null);
                                                            return;
                                                        }
                                                        setSeasons((prev) =>
                                                            prev.map((season, i) =>
                                                                i === seasonIndex
                                                                    ? {
                                                                        ...season,
                                                                        horizontal_trailer: [{ file, type: 0 }],
                                                                    }
                                                                    : season
                                                            )
                                                        );
                                                        toast.success("Uploading trailer... Please wait.");
                                                        setSeasonHorizontalTrailerUploaded(0);
                                                        setIsSubmitDisabled(true);
                                                        const ok = await uploadFile(
                                                            file,
                                                            "trailer",
                                                            "season",
                                                            (p) => setSeasonHorizontalTrailerUploaded(p)
                                                        );
                                                        setIsSubmitDisabled(false);
                                                        setSeasonHorizontalTrailerUploaded(ok ? true : false);
                                                        if (!ok) {
                                                            setSeasons((prev) =>
                                                                prev.map((season, i) =>
                                                                    i === seasonIndex
                                                                        ? { ...season, horizontal_trailer: [] }
                                                                        : season
                                                                )
                                                            );
                                                        }
                                                    }}
                                                    accept="video/mp4"
                                                    uploaded={seasonHorizontalTrailerUploaded}
                                                />
                                            </DistributeFormItem>
                                        </VideoField>
                                        <VideoField>
                                            <DistributeFormItem label="Season vertical trailer (optional)">
                                                <FileUpload
                                                    variant="dropzone"
                                                    file={
                                                        seasons[activeSeasonIndex]?.vertical_trailer?.[0]
                                                            ?.file || null
                                                    }
                                                    fileName={
                                                        findDistVideo("additional-trailer", "season")
                                                            ?.video_file_name ||
                                                        seasons[activeSeasonIndex]?.vertical_trailer?.[0]
                                                            ?.file?.name ||
                                                        undefined
                                                    }
                                                    fileUrl={
                                                        findDistVideo("additional-trailer", "season")
                                                            ?.video_id
                                                            ? `https://iframe.mediadelivery.net/embed/102339/${findDistVideo("additional-trailer", "season")!.video_id}?autoplay=false`
                                                            : null
                                                    }
                                                    onChange={async (file) => {
                                                        const seasonIndex = activeSeasonIndex;
                                                        if (!seasons[seasonIndex]) return;
                                                        if (!file) {
                                                            const removed = await deleteUploadedVideo("vertical_trailer", "season");
                                                            if (!removed) return;
                                                            setSeasons((prev) =>
                                                                prev.map((season, i) =>
                                                                    i === seasonIndex
                                                                        ? { ...season, vertical_trailer: [] }
                                                                        : season
                                                                )
                                                            );
                                                            setSeasonVerticalTrailerUploaded(null);
                                                            return;
                                                        }
                                                        setSeasons((prev) =>
                                                            prev.map((season, i) =>
                                                                i === seasonIndex
                                                                    ? {
                                                                        ...season,
                                                                        vertical_trailer: [{ file, type: 1 }],
                                                                    }
                                                                    : season
                                                            )
                                                        );
                                                        toast.success("Uploading trailer... Please wait.");
                                                        setSeasonVerticalTrailerUploaded(0);
                                                        setIsSubmitDisabled(true);
                                                        const ok = await uploadFile(
                                                            file,
                                                            "vertical_trailer",
                                                            "season",
                                                            (p) => setSeasonVerticalTrailerUploaded(p)
                                                        );
                                                        setIsSubmitDisabled(false);
                                                        setSeasonVerticalTrailerUploaded(ok ? true : false);
                                                        if (!ok) {
                                                            setSeasons((prev) =>
                                                                prev.map((season, i) =>
                                                                    i === seasonIndex
                                                                        ? { ...season, vertical_trailer: [] }
                                                                        : season
                                                                )
                                                            );
                                                        }
                                                    }}
                                                    accept="video/mp4"
                                                    uploaded={seasonVerticalTrailerUploaded}
                                                />
                                            </DistributeFormItem>
                                        </VideoField>
                                    </VideoQuadGrid>
                                </UploadSection>

                                <UploadSection>
                                    <UploadSectionTitle>Episode videos</UploadSectionTitle>
                                    <UploadSectionHint>
                                        Choose an episode, then upload horizontal (required) and vertical
                                        (optional) masters.
                                    </UploadSectionHint>
                                    <EpisodeChipRow>
                                        {seasons[activeSeasonIndex]?.episodes.map((ep, idx) => (
                                            <Button
                                                key={ep.id}
                                                size="large"
                                                type={
                                                    activeEpisodeIndex === idx ? "primary" : "default"
                                                }
                                                onClick={() => setActiveEpisodeIndex(idx)}
                                            >
                                                {ep.title || `Episode ${idx + 1}`}
                                            </Button>
                                        ))}
                                    </EpisodeChipRow>
                                    <VideoQuadGrid>
                                        <VideoField>
                                            <DistributeFormItem label="Episode horizontal video (required)">
                                                <FileUpload
                                                    variant="dropzone"
                                                    file={
                                                        seasons[activeSeasonIndex]?.episodes?.[
                                                            activeEpisodeIndex
                                                        ]?.horizontal_video?.[0]?.file || null
                                                    }
                                                    fileName={
                                                        findDistVideo("primary-video", "episode")
                                                            ?.video_file_name ||
                                                        seasons[activeSeasonIndex]?.episodes?.[
                                                            activeEpisodeIndex
                                                        ]?.horizontal_video?.[0]?.file?.name ||
                                                        undefined
                                                    }
                                                    fileUrl={
                                                        findDistVideo("primary-video", "episode")?.video_id
                                                            ? `https://iframe.mediadelivery.net/embed/102339/${findDistVideo("primary-video", "episode")!.video_id}?autoplay=false`
                                                            : null
                                                    }
                                                    onChange={async (file) => {
                                                        const newSeasons = [...seasons];
                                                        const season = newSeasons[activeSeasonIndex];
                                                        if (!season) return;
                                                        const episode = season.episodes[activeEpisodeIndex];
                                                        if (!episode) return;
                                                        if (!episode.horizontal_video)
                                                            episode.horizontal_video = [];
                                                        if (file) {
                                                            episode.horizontal_video[0] = { file, type: 0 };
                                                            toast.success("Uploading video... Please wait.");
                                                            setEpisodeHorizontalVideoUploaded(0);
                                                            setIsSubmitDisabled(true);
                                                            const ok = await uploadFile(
                                                                file,
                                                                "video",
                                                                "episode",
                                                                (p) =>
                                                                    setEpisodeHorizontalVideoUploaded(p)
                                                            );
                                                            setIsSubmitDisabled(false);
                                                            setEpisodeHorizontalVideoUploaded(
                                                                ok ? true : false
                                                            );
                                                        } else {
                                                            const removed = await deleteUploadedVideo("video", "episode");
                                                            if (!removed) return;
                                                            episode.horizontal_video = [];
                                                            setEpisodeHorizontalVideoUploaded(null);
                                                            setSeasons(newSeasons);
                                                            return;
                                                        }
                                                        setSeasons(newSeasons);
                                                    }}
                                                    accept="video/mp4"
                                                    uploaded={episodeHorizontalVideoUploaded}
                                                />
                                            </DistributeFormItem>
                                        </VideoField>
                                        <VideoField>
                                            <DistributeFormItem label="Episode vertical video (optional)">
                                                <FileUpload
                                                    variant="dropzone"
                                                    file={
                                                        seasons[activeSeasonIndex]?.episodes?.[
                                                            activeEpisodeIndex
                                                        ]?.vertical_video?.[0]?.file || null
                                                    }
                                                    fileName={
                                                        findDistVideo("additional-video", "episode")
                                                            ?.video_file_name ||
                                                        seasons[activeSeasonIndex]?.episodes?.[
                                                            activeEpisodeIndex
                                                        ]?.vertical_video?.[0]?.file?.name ||
                                                        undefined
                                                    }
                                                    fileUrl={
                                                        findDistVideo("additional-video", "episode")
                                                            ?.video_id
                                                            ? `https://iframe.mediadelivery.net/embed/102339/${findDistVideo("additional-video", "episode")!.video_id}?autoplay=false`
                                                            : null
                                                    }
                                                    onChange={async (file) => {
                                                        const newSeasons = [...seasons];
                                                        const season = newSeasons[activeSeasonIndex];
                                                        if (!season) return;
                                                        const episode = season.episodes[activeEpisodeIndex];
                                                        if (!episode) return;
                                                        if (!episode.vertical_video)
                                                            episode.vertical_video = [];
                                                        if (file) {
                                                            episode.vertical_video[0] = { file, type: 1 };
                                                            toast.success("Uploading video... Please wait.");
                                                            setEpisodeVerticalVideoUploaded(0);
                                                            setIsSubmitDisabled(true);
                                                            const ok = await uploadFile(
                                                                file,
                                                                "vertical_video",
                                                                "episode",
                                                                (p) => setEpisodeVerticalVideoUploaded(p)
                                                            );
                                                            setIsSubmitDisabled(false);
                                                            setEpisodeVerticalVideoUploaded(
                                                                ok ? true : false
                                                            );
                                                        } else {
                                                            const removed = await deleteUploadedVideo("vertical_video", "episode");
                                                            if (!removed) return;
                                                            episode.vertical_video = [];
                                                            setEpisodeVerticalVideoUploaded(null);
                                                            setSeasons(newSeasons);
                                                            return;
                                                        }
                                                        setSeasons(newSeasons);
                                                    }}
                                                    accept="video/mp4"
                                                    uploaded={episodeVerticalVideoUploaded}
                                                />
                                            </DistributeFormItem>
                                        </VideoField>
                                    </VideoQuadGrid>
                                </UploadSection>
                            </UploadShell>
                            <UploadActions>
                                <SubmitButton
                                    variant="inline"
                                    label={uploadsInProgress ? "Uploading..." : "Submit"}
                                    disabled={uploadsInProgress || !requiredVideosReady}
                                    onClick={() => {
                                        if (!requiredVideosReady) {
                                            toast.error(
                                                "Upload the series trailer, season trailer, and every episode video first."
                                            );
                                            return;
                                        }
                                        toast.success("Series submitted for distribution.");
                                        navigate(`/${film?.custom_name || seriesId}`, {
                                            replace: true,
                                        });
                                    }}
                                />
                            </UploadActions>
                            </>
                        ),
                    },
                ]}
            />
        </Main>
    );
};

const DISTRIBUTE_PAGE_MAX = "min(94vw, 172rem)";

const Main = styled.main`
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 100dvh;
  padding: 8.5rem clamp(1.2rem, 3vw, 3.2rem) 2.4rem clamp(1.2rem, 3vw, 3.2rem);
  padding-right: 8rem;
  width: 100%;
  max-width: 100%;
  background-color: ${colors.lightBackground.hex};

  .distribute-tabs.ant-tabs {
    flex: 1 1 auto;
    min-height: min-content;
    display: flex !important;
    flex-direction: column;
    max-width: ${DISTRIBUTE_PAGE_MAX};
    width: 100%;
    margin: 0 auto;
  }

  .distribute-tabs.ant-tabs .ant-tabs-content-holder {
    flex: 1 1 auto;
    min-height: min-content;
    overflow: visible !important;
  }

  .distribute-tabs.ant-tabs .ant-tabs-content,
  .distribute-tabs.ant-tabs .ant-tabs-tabpane {
    height: auto;
    overflow: visible !important;
  }

  .distribute-tabs.ant-tabs .ant-tabs-tabpane-active {
    display: flex;
    flex-direction: column;
    min-height: 100%;
  }

  .distribute-form.ant-form-vertical .ant-form-item:not(.ant-form-item-horizontal)
    .ant-form-item-row {
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .distribute-form .ant-form-item-vertical .ant-form-item-row {
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .distribute-form .ant-form-item-control-input-content {
    width: 100%;
  }

  .distribute-form.ant-form .ant-form-item .ant-checkbox-wrapper {
    white-space: normal;
    align-items: flex-start;
  }

  .distribute-tabs .ant-tabs-nav {
    margin-bottom: 0.6rem;
    flex-shrink: 0;
  }

  .distribute-tabs .ant-tabs-nav::before {
    border-bottom: none;
  }

  .distribute-tabs .ant-tabs-tab {
    font-size: 1.65rem;
    padding: 0.55rem 0;
    color: ${colors.text.hex};
    opacity: 0.55;
  }

  .distribute-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${colors.text.hex};
    font-weight: 600;
    opacity: 1;
  }

  .distribute-tabs .ant-tabs-tab-disabled {
    opacity: 0.35;
  }

  .distribute-tabs .ant-tabs-ink-bar {
    height: 3px;
    border-radius: 999px;
    background: ${colors.primary.hex};
  }

  .distribute-form.ant-form {
    flex: 1 1 auto;
    min-height: min-content;
    display: flex;
    flex-direction: column;
  }

  .distribute-form.ant-form .ant-form-item {
    margin-bottom: 1.6rem !important;
  }

  .distribute-rights-form.ant-form .ant-form-item {
    margin-bottom: 0.85rem !important;
  }

  .distribute-rights-form .ant-form-item-label {
    padding-bottom: 0.15rem !important;
  }

  .distribute-uploads .ant-form-item-control-input-content > div {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    min-height: 14rem !important;
    box-sizing: border-box;
  }

  .distribute-uploads .ant-form-item-label {
    min-height: 2.8em;
  }

  .distribute-form.ant-form .ant-form-item-label > label {
    font-size: 1.35rem !important;
    font-weight: 600 !important;
    color: ${colors.text.hex} !important;
    height: auto !important;
  }

  .distribute-rights-form .ant-checkbox-wrapper {
    font-size: 1.35rem;
    line-height: 1.45;
    align-items: flex-start;
    color: ${colors.text.hex};
  }

  .distribute-rights-form .ant-checkbox-wrapper + .ant-checkbox-wrapper {
    margin-top: 0;
    margin-inline-start: 0 !important;
  }

  .distribute-rights-form .rights-ack .ant-form-item {
    margin-bottom: 0.35rem !important;
  }

  .distribute-rights-form .rights-ack .ant-form-item:has(.ant-input) {
    margin-top: 0.7rem;
    margin-bottom: 1rem !important;
  }

  @media (max-width: 72em), (max-height: 40em) {
    padding-bottom: 3rem;
  }

  .ant-form-item-has-error {
    margin-bottom: 2.4rem !important;
  }
`;

const DistributeHero = styled.div`
  max-width: ${DISTRIBUTE_PAGE_MAX};
  width: 100%;
  margin: 0 auto 0.7rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  flex-shrink: 0;
`;

const HeroTop = styled(Row).attrs({ $full: true })`
  width: 100%;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 1.6rem 2rem;
`;

const Title = styled.h1`
  margin: 0;
  flex: 1;
  min-width: min(100%, 28rem);
  font-size: clamp(2.6rem, 4vw, 3.8rem);
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: ${colors.text.hex};
`;

const SeriesName = styled.span`
  font-weight: 600;
  color: ${colors.primary.hex};
`;

const Paragraph = styled.p`
  margin: 0;
  max-width: min(96rem, 100%);
  font-size: 1.55rem;
  line-height: 1.55;
  font-weight: 400;
  color: ${colors.text.hex};
  opacity: 0.82;
`;

const SeasonToolbar = styled.div`
  width: min(36rem, 100%);
  margin-left: auto;
  flex: 0 0 auto;

  @media (max-width: 48em) {
    width: 100%;
    margin-left: 0;
  }

  .ant-form-item {
    margin-bottom: 0 !important;
  }
`;

const RightsShell = styled.div`
  max-width: ${DISTRIBUTE_PAGE_MAX};
  margin: 0 auto;
  padding: 1.4rem 1.8rem 1.2rem;
  background-color: ${colors.background.hex};
  border: 1px solid ${colors.grey.hex};
  border-radius: 1.2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr);
  gap: 1.6rem;
  width: 100%;
  min-width: 0;
  flex: 1 1 auto;
  min-height: min-content;
  align-items: stretch;

  @media (max-width: 72em), (max-height: 40em) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

const RightsColumn = styled.div`
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const AckPanel = styled.div.attrs({ className: "rights-ack" })`
  flex: 1 1 auto;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  padding: 1.4rem 1.6rem 1.8rem;
  border-radius: 0.9rem;
  background: ${colors.isDark ? "#02060b" : "#f4f7fb"};
  border: 1px solid ${colors.isDark ? "rgba(242, 247, 252, 0.1)" : colors.grey.hex};
`;

const AckHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin: 0 0 0.9rem;
  font-size: 1.35rem;
  font-weight: 600;
  line-height: 1.3;
  color: ${colors.text.hex};
`;

const RequiredStar = styled.span`
  color: ${colors.error.hex};
  font-size: 1.35rem;
  line-height: 1;
`;

const RightsSubmit = styled.div`
  margin-top: 0.4rem;
  display: flex;
  justify-content: center;

  button {
    margin: 0;
    width: auto;
    min-width: min(100%, 24rem);
    max-width: 100%;
  }
`;

const UploadActions = styled.div`
  width: 100%;
  max-width: ${DISTRIBUTE_PAGE_MAX};
  margin: 1.6rem auto 0;
  display: flex;
  justify-content: flex-end;

  button {
    margin: 0;
    width: auto;
    min-width: 16rem;
  }
`;

const UploadShell = styled.div<{ $columns?: number }>`
  width: 100%;
  max-width: ${DISTRIBUTE_PAGE_MAX};
  margin: 0 auto;
  flex: 0 0 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns ?? 3}, minmax(0, 1fr));
  gap: 1.2rem;
  align-items: stretch;

  @media (max-width: 70em) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const UploadSection = styled.section`
  width: 100%;
  min-width: 0;
  padding: 1.2rem 1.4rem;
  min-height: 0;
  align-self: stretch;
  background-color: ${colors.background.hex};
  border: 1px solid ${colors.grey.hex};
  border-radius: 1.2rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const UploadSectionTitle = styled.h3`
  margin: 0 0 0.5rem;
  font-size: 1.55rem;
  font-weight: 600;
  color: ${colors.text.hex};
  letter-spacing: 0.01em;
`;

const UploadSectionHint = styled.p`
  margin: 0 0 0.8rem;
  font-size: 1.3rem;
  line-height: 1.5;
  color: ${colors.text.hex};
  opacity: 0.78;
  max-width: 100%;
  min-height: calc(1.3rem * 1.5 * 3);
`;

const EpisodeChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 0.6rem;
`;

const VideoQuadGrid = styled.div.attrs({
  className: "distribute-video-quad-grid",
})`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.5rem 1.75rem;
  width: 100%;
  min-height: 0;
  margin-top: auto;
  align-items: stretch;

  @media (max-width: 48em) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const VideoField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  min-height: 0;
  height: 100%;
  align-self: stretch;
  justify-self: stretch;

  && .ant-form-item {
    flex: 1 1 auto !important;
    display: flex !important;
    flex-direction: column;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    min-height: 0;
    margin-bottom: 0 !important;
  }

  && .ant-form-item-row {
    flex: 1 1 auto !important;
    flex-direction: column !important;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    min-height: 0 !important;
    align-items: stretch !important;
  }

  && .ant-form-item-control {
    flex: 1 1 auto !important;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    min-height: 0 !important;
  }

  && .ant-form-item-control-input,
  && .ant-form-item-control-input-content {
    flex: 1 1 auto !important;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }

  && .ant-form-item-label {
    display: flex;
    align-items: flex-end;
    min-height: calc(1.35rem * 1.35 * 3 + 0.45rem);
    padding-bottom: 0.45rem;
    flex-shrink: 0;
  }

  && .ant-form-item-label > label {
    white-space: normal;
    line-height: 1.35;
    height: auto;
    align-items: flex-end;
  }
`;
